import { T } from '@tolgee/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import { getTrackingParams } from '../../../utils/adsConversionTrackingUtils';
import { QUOTE_REQUEST_HREF } from './PlanOverviewSection';
import { MOBILE_QUERY, NARROW_QUERY } from './pricingTokens';

export const FinalCtaSection = () => {
  const metrics = useMetrics(true);
  const router = useRouter();

  const handleSignup = () => {
    metrics.logEvent('PricingPage.FinalCta.Signup.clicked');
    const trackingParams = getTrackingParams();
    const query: Record<string, string> = { callback: '/' };
    if (trackingParams.fbclid) query.fbclid = trackingParams.fbclid;
    if (trackingParams.ttclid) query.ttclid = trackingParams.ttclid;
    router.push({ pathname: '/signup', query });
  };

  return (
    <Section>
      <Heading>
        <T
          keyName="Pricing.FinalCta.Heading"
          defaultValue="Make writing practice a daily habit"
        />
      </Heading>
      <Body>
        <T
          keyName="Pricing.FinalCta.Body"
          defaultValue="Every student writes. Every student gets feedback. Free for teachers, unlimited for schools — and students never need an account."
        />
      </Body>
      <Actions>
        <PrimaryButton type="button" onClick={handleSignup}>
          <T keyName="Pricing.FinalCta.Signup" defaultValue="Sign up — it's free" />
        </PrimaryButton>
        {/* passHref: next/link only forwards href to a literal <a>. */}
        <Link href={QUOTE_REQUEST_HREF} passHref>
          <SecondaryLink
            onClick={() => metrics.logEvent('PricingPage.FinalCta.RequestQuote.clicked')}
          >
            <T keyName="Pricing.FinalCta.Quote" defaultValue="Request a quote" />
          </SecondaryLink>
        </Link>
      </Actions>
    </Section>
  );
};

const Section = styled.section`
  width: 100%;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  padding: 80px 32px;
  text-align: center;

  @media ${MOBILE_QUERY} {
    padding: 60px 20px;
  }
`;

const Heading = styled.h2`
  font-size: clamp(36px, 4.4vw, 56px);
  font-weight: 900;
  letter-spacing: -0.02em;
  margin: 0 0 14px;
  line-height: 1.05;
  color: ${theme.colors.white};

  @media ${NARROW_QUERY} {
    font-size: 30px;
  }
`;

const Body = styled.p`
  && {
    font-size: 18px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
    max-width: 640px;
    margin: 0 auto;
    line-height: 1.5;
  }

  @media ${NARROW_QUERY} {
    && {
      font-size: 16px;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 14px;
  justify-content: center;
  margin-top: 28px;
  flex-wrap: wrap;
`;

const ctaBase = `
  font-weight: 900;
  font-size: 16px;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  display: inline-block;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
`;

const PrimaryButton = styled.button`
  ${ctaBase}
  background: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border: 2px solid ${theme.colors.white};
  padding: 12px 20px;

  &:hover {
    background: ${theme.colors.black};
    border-color: ${theme.colors.black};
    color: ${theme.colors.white};
  }
`;

const SecondaryLink = styled.a`
  ${ctaBase}
  border: 2px solid ${theme.colors.white};
  color: ${theme.colors.white};
  background: transparent;
  padding: 12px 20px;

  &:hover {
    background: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }
`;

export default FinalCtaSection;
